import { Check, CircleSlash, RotateCcw, Terminal } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Spinner,
} from "@opencloud/ui";
import {
  buildToolActivityView,
  formatToolDuration,
  readableToolName,
  type PresentedToolCall,
  type ToolActivityLifecycle,
} from "../tool-activity.js";
import type { ConversationToolCall } from "../types.js";
import { CopyableCode } from "./CopyableCode.js";

export interface ToolUIPartIdentity {
  messageId: string;
  partId: string;
  state: string;
}

export function ToolActivityCard({
  calls,
  active,
  defaultExpanded = false,
  defaultCallDetailsExpanded = false,
  uiPartIdentities,
  onRetry,
}: {
  calls: readonly ConversationToolCall[];
  active: boolean;
  defaultExpanded?: boolean;
  defaultCallDetailsExpanded?: boolean;
  uiPartIdentities?: ReadonlyMap<string, ToolUIPartIdentity>;
  onRetry?: () => void;
}) {
  const view = buildToolActivityView(calls, active);
  const soleCall = calls.length === 1 ? calls[0] : undefined;
  const soleIdentity = soleCall
    ? uiPartIdentities?.get(soleCall.id)
    : undefined;

  return (
    <Accordion
      className="a3-tool-activity-card"
      data-lifecycle={view.lifecycle}
      data-tool-call-count={calls.length}
      data-tool-call-id={soleCall?.id}
      data-status={soleCall?.status}
      data-ui-message-id={soleIdentity?.messageId}
      data-ui-part-id={soleIdentity?.partId}
      data-ui-part-type={soleIdentity ? "dynamic-tool" : undefined}
      data-ui-part-state={soleIdentity?.state}
      aria-label="Agent activity"
      defaultValue={defaultExpanded ? ["tool-activity"] : undefined}
    >
      <AccordionItem value="tool-activity">
        <AccordionTrigger className="a3-tool-activity-summary">
          <span className="a3-tool-activity-state-icon" aria-hidden="true">
            <LifecycleIcon lifecycle={view.lifecycle} />
          </span>
          <span className="a3-tool-activity-copy">
            <strong aria-live="polite" aria-atomic="true">
              {view.latest.summary}
            </strong>
            <small>{view.countLabel} · {view.statusLabel}</small>
          </span>
        </AccordionTrigger>
        {view.lifecycle === "failed" && onRetry ? (
          <Button type="button" variant="ghost" onClick={onRetry}><RotateCcw aria-hidden="true" />Retry from saved work</Button>
        ) : null}
        <AccordionContent className="a3-tool-activity-details">
          <div className="a3-tool-activity-history-heading">
            <div>
              <strong>Activity</strong>
              <span>{view.detailLabel}</span>
            </div>
            <small>Newest last</small>
          </div>

          <ol className="a3-tool-activity-list">
            {view.calls.map((presented) => (
              <ReadableCall
                key={presented.call.id}
                presented={presented}
                active={active}
                defaultExpanded={defaultCallDetailsExpanded}
                {...(uiPartIdentities?.get(presented.call.id)
                  ? { identity: uiPartIdentities.get(presented.call.id)! }
                  : {})}
              />
            ))}
          </ol>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ReadableCall({
  presented,
  active,
  defaultExpanded,
  identity,
}: {
  presented: PresentedToolCall;
  active: boolean;
  defaultExpanded: boolean;
  identity?: ToolUIPartIdentity;
}) {
  const { call, summary } = presented;
  return (
    <li
      data-status={call.status}
      data-tool-call-id={call.id}
      data-ui-message-id={identity?.messageId}
      data-ui-part-id={identity?.partId}
      data-ui-part-type={identity ? "dynamic-tool" : undefined}
      data-ui-part-state={identity?.state}
    >
      <Accordion
        className="a3-tool-activity-call"
        defaultValue={defaultExpanded ? [call.id] : undefined}
      >
        <AccordionItem value={call.id}>
          <AccordionTrigger
            className="a3-tool-activity-call-summary"
            aria-label={`Show details for ${summary}`}
          >
            <span className="a3-tool-activity-call-icon" aria-hidden="true">
              <CallIcon status={call.status} active={active} />
            </span>
            <span className="a3-tool-activity-call-copy">
              <strong>{summary}</strong>
              <small>{!active && call.status === "running" ? "No final result" : readableStatus(call)}</small>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <TechnicalCall call={call} active={active} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </li>
  );
}

function TechnicalCall({ call, active }: { call: ConversationToolCall; active: boolean }) {
  const input =
    call.kind === "command" ? (call.input ?? call.summary) : call.input;

  return (
    <div
      className="a3-tool-call-details"
      data-status={call.status}
      data-kind={call.kind}
    >
      <p className="a3-tool-readable-detail">{!active && call.status === "running" ? "No final result was recorded for this action." : toolReadableDetail(call)}</p>
      <div className="a3-tool-call-details-heading">
        <div>
          <strong>{readableToolName(call.tool)}</strong>
          <code>{call.tool}</code>
        </div>
        <span>{!active && call.status === "running" ? "No final result" : technicalStatus(call)}</span>
      </div>
      <section>
        {input ? (
          <ToolPayload value={input} label={call.kind === "command" ? "Command" : "Tool input"} action={call.summary} />
        ) : (
          <p>No input was retained for this action.</p>
        )}
      </section>
      <section>
        {call.output ? (
          <ToolPayload value={call.output} label="Result" action={call.summary} />
        ) : call.status === "running" && active ? (
          <p>Output will appear when this action finishes.</p>
        ) : (
          <p>No text output was retained for this action.</p>
        )}
      </section>
      {call.errorClass ? (
        <p className="a3-tool-call-details-error">
          Failure type: <code>{call.errorClass}</code>
        </p>
      ) : null}
    </div>
  );
}

function LifecycleIcon({ lifecycle }: { lifecycle: ToolActivityLifecycle }) {
  if (lifecycle === "completed") return <Check />;
  if (lifecycle === "failed") return <Terminal />;
  if (lifecycle === "cancelled" || lifecycle === "interrupted") return <CircleSlash />;
  return <Spinner />;
}

function CallIcon({ status, active }: { status: ConversationToolCall["status"]; active: boolean }) {
  if (status === "running") return active ? <Spinner /> : <CircleSlash />;
  if (status === "failed") return <Terminal />;
  if (status === "cancelled") return <CircleSlash />;
  return <Check />;
}

function ToolPayload({ value, label, action }: { value: string; label: string; action: string }) {
  const total = truncatedTotal(value);
  return (
    <>
      <CopyableCode value={value} label={label} action={action} />
      {total ? (
        <p className="a3-tool-payload-notice">
          <Badge tone="neutral">Truncated</Badge>
          This retained record is shortened from {total.toLocaleString()}{" "}
          characters.
        </p>
      ) : null}
    </>
  );
}

function truncatedTotal(value: string): number | null {
  const match = value.match(/… truncated \(([\d,]+) characters total\)\s*$/);
  if (!match?.[1]) return null;
  const total = Number(match[1].replaceAll(",", ""));
  return Number.isFinite(total) ? total : null;
}

function toolReadableDetail(call: ConversationToolCall): string {
  if (call.status === "running") return "This action is still running.";
  if (call.status === "failed") return "This action failed. No successful result was recorded.";
  if (call.status === "cancelled")
    return "This action stopped before it finished.";
  return "This action finished.";
}

function readableStatus(call: ConversationToolCall): string {
  if (call.status === "running") return "Now";
  const duration =
    call.durationMs === null ? "" : ` · ${formatToolDuration(call.durationMs)}`;
  if (call.status === "cancelled") return `Stopped${duration}`;
  if (call.status === "failed") return `Failed${duration}`;
  return `Finished${duration}`;
}

function technicalStatus(call: ConversationToolCall): string {
  const duration =
    call.durationMs === null ? "" : ` · ${formatToolDuration(call.durationMs)}`;
  if (call.status === "running") return "Running";
  if (call.status === "cancelled") return `Stopped${duration}`;
  if (call.status === "failed") return `Failed${duration}`;
  return `Finished${duration}`;
}
